import React from "react";
import PropTypes from "prop-types";
import { interactive, textColors, typography } from "../../../design-system/tokens";
import { formatOriginDetail, getOriginMeta } from "./messagesUtils";

/**
 * Where a message came from, as two lines:
 *   Automation → the automation name (a button that applies the automation
 *                filter when `onAutomationSelect` is given) + "Automation · slug"
 *   Broadcast  → "Broadcast" (+ humanised origin_detail when it says more)
 *   System     → humanised origin_detail, e.g. "Verification code" + "System"
 */
const OriginLabel = ({ message, onAutomationSelect, className = "" }) => {
  const origin = message?.origin || "";
  const meta = getOriginMeta(origin);
  const detail = message?.origin_detail || "";
  const automation = message?.automation || null;

  let primary;
  let secondary;

  if (origin === "automation") {
    if (automation) {
      primary = onAutomationSelect ? (
        <button
          type="button"
          onClick={() => onAutomationSelect(automation)}
          className={`text-left font-medium ${interactive.link} ${interactive.linkHover} hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 rounded-sm break-words`}
          title={`Show only messages from “${automation.name}”`}
        >
          {automation.name}
        </button>
      ) : (
        <span className="font-medium break-words">{automation.name}</span>
      );
      secondary = `${meta.label}${automation.slug ? ` · ${automation.slug}` : ""}`;
    } else {
      primary = <span className={textColors.muted}>Deleted automation</span>;
      secondary = meta.label;
    }
  } else if (origin === "broadcast") {
    primary = <span className="font-medium">{meta.label}</span>;
    secondary = detail && detail !== "admin_broadcast" ? formatOriginDetail(detail) : "";
  } else if (origin === "system") {
    primary = <span className="font-medium">{formatOriginDetail(detail) || meta.label}</span>;
    secondary = meta.label;
  } else {
    primary = <span className="font-medium">{meta.label}</span>;
    secondary = detail ? formatOriginDetail(detail) : "";
  }

  return (
    <div className={`min-w-0 ${className}`} title={meta.description || undefined}>
      <div className={`${typography.bodySmall} ${textColors.primary}`}>{primary}</div>
      {secondary ? <div className={`${typography.caption} ${textColors.tertiary} break-words`}>{secondary}</div> : null}
    </div>
  );
};

OriginLabel.propTypes = {
  message: PropTypes.shape({
    origin: PropTypes.string,
    origin_detail: PropTypes.string,
    automation: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, slug: PropTypes.string }),
  }),
  /** Called with the automation ref when the admin clicks an automation name. */
  onAutomationSelect: PropTypes.func,
  className: PropTypes.string,
};

export default OriginLabel;
